import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import { RbacPermissions } from '../../../core/interfaces/user';
import { CurrentPlanService } from '../../../core/services/current-plan.service';
import { RefreshService } from '../../../core/services/refresh.service';
import useSettingsStorageService from '../../../auth/hooks/useSettingsStorageService';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import { useQuery } from '../../../core/hooks/useQuery';
import useService from '../../../injector/hooks/useService';
import useTeamSettings from '../../team/hooks/useTeamSettingsService';
import { BillingNavValue, BillingPlanState } from './interfaces';
import { buildBillingTabDescriptors } from './billingTabs';
import { USER_ENTERED_CARD_DETAILS_KEY } from '../constants';
import PaymentAPI from '../PaymentAPI';

const INITIAL_VALUE: BillingNavValue = {
  resolved: false,
  retrying: false,
  error: null,
  refetch: () => {},
  plan: {
    initialNav: false,
    showNoPlanMessage: false,
    initialSetupRequired: false,
    accessDenied: false,
    failed: false
  },
  tabs: []
};

const BillingNavContext = createContext<BillingNavValue>(INITIAL_VALUE);

/**
 * Resolves the Billing section once for the whole settings shell.
 *
 * The plan fetch used to live on the Billing page, so the sidebar drew a resting list until the
 * user opened Billing and only then learnt which rows this team actually gets — five rows
 * collapsing to two on the first click. Mounted here it runs when Settings opens, and because
 * the settings layout route stays mounted across tab changes it runs exactly once: moving
 * between sections re-reads this value rather than re-deciding it.
 */
export function BillingNavProvider({ children }: { children: React.ReactNode }) {
  const currentPlanService = useService(CurrentPlanService);
  const refreshService = useService(RefreshService);
  const settingsStorageService = useSettingsStorageService();
  const { isWorkspaceTeam } = useTeamSettings();
  const { isOwnerOrHasPermission, isInternalUserOrHasPermission, hasPermission } =
    useHasPermission();
  const canViewBilling = isInternalUserOrHasPermission(RbacPermissions.BILLING_EDIT);
  const canEditBilling = isOwnerOrHasPermission(RbacPermissions.BILLING_EDIT);

  const [enteredCardDetails, setEnteredCardDetails] = useState(false);

  const { data, uiState, fetch, refetch, writeData } = useQuery<BillingPlanState>({
    dataSource$: () => {
      if (isWorkspaceTeam) {
        return Promise.resolve({
          currentPlan: null,
          initialNav: false,
          showNoPlanMessage: false,
          initialSetupRequired: false,
          accessDenied: false,
          failed: false
        });
      }

      return currentPlanService
        .getCurrentPlan()
        .toPromise()
        .then(res => {
          const noPlan = !res.data || !Object.keys(res.data).length;

          return {
            currentPlan: res.data,
            initialNav: noPlan,
            showNoPlanMessage: noPlan,
            initialSetupRequired: false,
            accessDenied: false,
            failed: false
          };
        })
        .catch(err => {
          if (err.status === 404) {
            if (isOwnerOrHasPermission(RbacPermissions.BILLING_EDIT)) {
              // The page turns this into a redirect to Plan Selection; doing it here would
              // drag a user sitting on another settings page into Billing.
              return {
                initialNav: true,
                showNoPlanMessage: false,
                initialSetupRequired: true,
                accessDenied: false,
                failed: false
              };
            }

            return {
              initialNav: false,
              showNoPlanMessage: true,
              initialSetupRequired: false,
              accessDenied: false,
              failed: false
            };
          }

          /*
           * A 403 here is the expected answer for a role that may not read the plan, not a
           * failure: it resolves normally so the section keeps the rows that role is allowed
           * (Usage Summary), rather than falling into the error state.
           */
          if (err.status === 403) {
            return {
              initialNav: false,
              showNoPlanMessage: false,
              initialSetupRequired: false,
              accessDenied: !hasPermission(RbacPermissions.BILLING_EDIT),
              failed: false
            };
          }

          /*
           * Any other failure is reported as state rather than rethrown: the section degrades
           * to its plan-independent row and the Billing page offers a retry, instead of the
           * query tearing down and taking the whole sidebar section with it.
           */
          return {
            error: err,
            initialNav: false,
            showNoPlanMessage: false,
            initialSetupRequired: false,
            accessDenied: false,
            failed: true
          };
        });
    }
  });

  const { data: paymentMethods, fetch: fetchPaymentMethods } = useQuery({
    // Cards are only used to decide whether the history rows exist. A role that may not read
    // them gets a 403, which means "no cards to show", not "the section is broken".
    dataSource$: () => PaymentAPI.getCards().then(res => res.data).catch(() => [])
  });

  /** Latches on the first response, success or failure: the rows shimmer once and then settle. */
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (data) {
      setResolved(true);
    }
  }, [data]);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    if (isWorkspaceTeam) {
      return undefined;
    }

    const sub = currentPlanService.currentPlan$.subscribe(plan => {
      // A tab that changes the plan writes back through here, so the sidebar follows without
      // another request. The subject replays its current value on subscribe, which is null
      // before the fetch lands — that is not a plan change.
      if (!plan) {
        return;
      }

      writeData(previous => ({
        ...previous,
        currentPlan: plan,
        initialNav: false,
        showNoPlanMessage: false
      }));
    });

    return () => sub.unsubscribe();
  }, []);

  // Cards only decide whether the history rows exist; they never matter when every plan row is hidden.
  const cardsMatter = !isWorkspaceTeam && canViewBilling;

  useEffect(() => {
    if (!cardsMatter) {
      return undefined;
    }

    const sub = refreshService.refreshCardDetailsSubmit$.subscribe(() => {
      fetchPaymentMethods();
    });

    return () => sub.unsubscribe();
  }, [cardsMatter]);

  useEffect(() => {
    if (settingsStorageService.getSettings(USER_ENTERED_CARD_DETAILS_KEY)) {
      setEnteredCardDetails(true);
    } else if (cardsMatter) {
      fetchPaymentMethods();
    }
  }, [cardsMatter]);

  useEffect(() => {
    if (paymentMethods?.length) {
      settingsStorageService.applySettings(USER_ENTERED_CARD_DETAILS_KEY, true);
      setEnteredCardDetails(true);
    }
  }, [paymentMethods?.length]);

  const plan = data ?? INITIAL_VALUE.plan;

  const tabs = useMemo(
    () =>
      buildBillingTabDescriptors({
        initialNav: plan.initialNav,
        enteredCardDetails,
        currentPlan: plan.currentPlan,
        isWorkspaceTeam: !!isWorkspaceTeam,
        canViewBilling,
        canEditBilling,
        planUnavailable: plan.failed
      }),
    [plan, enteredCardDetails, isWorkspaceTeam, canViewBilling, canEditBilling]
  );

  const value = useMemo<BillingNavValue>(
    () => ({
      resolved: resolved || !!isWorkspaceTeam,
      // `refetch`, not `fetch`: it keeps the last answer while the request runs, so a retry never
      // renders the page or the sidebar from an empty plan.
      retrying: uiState === EntityUIState.REFRESHING,
      error: (plan as BillingPlanState & { error?: { status: number } }).error ?? null,
      refetch,
      plan,
      tabs
    }),
    [resolved, isWorkspaceTeam, uiState, plan, tabs, refetch]
  );

  return <BillingNavContext.Provider value={value}>{children}</BillingNavContext.Provider>;
}

export function useBillingNav(): BillingNavValue {
  return useContext(BillingNavContext);
}
